import { Form } from "@remix-run/react";
import clsx from "clsx";
import { FormEventHandler, useEffect, useState } from "react";
import { ClientSide } from "~/@types";
import { IInArticleNewsletterFields } from "~/@types/generated/contentful";
import { TextCta } from "~/components/ctas";
import { Arrow } from "~/components/icons";

import {
  CaptureConfig,
  D365MktFormCapture,
  Mapping,
} from "./formCapture.types";

interface NewsletterFormProps {
  fields: ClientSide<IInArticleNewsletterFields>;
}

declare global {
  interface Window {
    d365mktformcapture: D365MktFormCapture | undefined;
  }
}

// Define the mappings
const mappings: Mapping[] = [
  {
    FormFieldName: "newsletter_first_name",
    DataverseFieldName: "firstname",
  },
  {
    FormFieldName: "newsletter_last_name",
    DataverseFieldName: "lastname",
  },
  {
    FormFieldName: "newsletter_email",
    DataverseFieldName: "emailaddress1",
  },
  {
    FormFieldName: "newsletter_contact_type",
    DataverseFieldName: "c9_contacttype",
    DataverseFieldValue: [
      { FormValue: "public", DataverseValue: "794540000" }, // Public
      { FormValue: "", DataverseValue: "794540001" }, // Stakeholder
    ],
  },
  {
    FormFieldName: "page_signed_up_on",
    DataverseFieldName: "tbi_sourcewebpage",
  },
  {
    FormFieldName: "newsletter_consent",
    DataverseFieldName: "msdynmkt_topicid;channels;optinwhenchecked",
    DataverseFieldValue: "ef597b17-17ee-ef11-9341-6045bdc1cb95;Email;true",
  },
];

const captureConfig: CaptureConfig = {
  FormId: "64e91c9a-b0e6-ee11-904d-6045bdcf56d4",
  FormApiUrl:
    "https://public-gbr.mkt.dynamics.com/api/v1.0/orgs/7a2dad3e-a1b3-4828-98dd-e6bd8c664cde/landingpageforms",
};

export default function InArticleNewsletterD365({
  fields,
}: NewsletterFormProps) {
  const [submitted, setSubmitted] = useState(false);

  function submitNewsletter() {
    setSubmitted(true);
    console.log("Newsletter submitted");
  }

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    submitNewsletter();
    if (!window.d365mktformcapture) {
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    const serializedForm = window.d365mktformcapture.serializeForm(
      form,
      mappings,
    );
    const payload = serializedForm.SerializedForm.build();
    try {
      await window.d365mktformcapture.submitForm(captureConfig, payload);
    } catch (e) {
      console.log(e, "error");
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/gbr/FormCapture/FormCapture.bundle.js`;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  if (submitted) {
    return (
      <div className="col-span-full mt-8 flex h-[415px] items-center justify-center bg-black py-10 px-4 text-white lg:col-span-6 lg:col-start-4 lg:h-[352px] lg:p-10">
        <h5 className="text-center">Thank you for subscribing!</h5>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={"flex-center col-span-full mb-20 aspect-square flex-col"}>
        <h3 className={clsx("mb-10 max-w-[410px] text-center")}>
          Thank you for subscribing!
        </h3>
      </div>
    );
  }

  return (
    <Form
      className="col-span-full m-auto mt-8 bg-black py-10 px-4 text-white lg:col-span-6 lg:col-start-4 lg:p-10"
      onSubmit={handleSubmit}
      id="in_article_newsletter_form"
    >
      <h5 className="mb-[22px] lg:mb-10">{fields.heading}</h5>
      <div className="flex flex-col lg:flex-row lg:gap-4">
        <label htmlFor="newsletter_first_name" className="flex-1">
          <input
            id="newsletter_first_name"
            name="newsletter_first_name"
            type={"text"}
            required
            title="Please enter at least 2 letters."
            placeholder="First name *"
            className={clsx(
              "body border-b-1 col-span-4 w-full border-0 border-b border-solid bg-black py-1 outline-0 outline-offset-4 outline-slate placeholder:text-white focus:outline-1 lg:col-start-2",
            )}
          />
          <span className="meta-small text-white opacity-0 transition-opacity peer-focus:opacity-100">
            First Name *
          </span>
        </label>
        <label htmlFor="newsletter_last_name" className="flex-1">
          <input
            id="newsletter_last_name"
            name="newsletter_last_name"
            type={"text"}
            title="Please enter at least 2 letters."
            required
            placeholder="Last name *"
            className={clsx(
              "body border-b-1 col-span-4 w-full border-0 border-b border-solid bg-black py-1 outline-0 outline-offset-4 outline-slate placeholder:text-white focus:outline-1 lg:col-start-2",
            )}
          />
          <span className="meta-small text-white opacity-0 transition-opacity peer-focus:opacity-100">
            Last Name *
          </span>
        </label>
      </div>
      <input
        type={"email"}
        id="newsletter_email"
        name="newsletter_email"
        className="body border-b-1 col-span-4 w-full border-0 border-b border-solid border-b-white bg-black py-1 outline-0 outline-offset-4 outline-slate placeholder:text-white focus:outline-1 lg:col-start-2"
        placeholder="Email *"
        required
      />
      <div className="my-10 flex items-start">
        <input
          type={"checkbox"}
          id="newsletter_consent"
          name="newsletter_consent"
          className="mr-3 border border-solid border-black"
          checked
          readOnly
          hidden
        />
        <label
          htmlFor="newsletter_consent"
          className="aeonik-base text-[10px] text-dark_grey"
        >
          By signing up, you agree to receive emails about the work of Tony
          Blair and the Tony Blair Institute. For full information on the use of
          your data please see our{" "}
          <a
            target="__blank"
            href={fields.privacyPolicyUrl}
            className="underline"
          >
            privacy policy
          </a>
          .
        </label>
      </div>
      <select
        id="newsletter_contact_type"
        name="newsletter_contact_type"
        className="hidden"
        required
        defaultValue="public"
      >
        <option>public</option>
      </select>
      <input
        type="hidden"
        name="page_signed_up_on"
        value={typeof window !== "undefined" ? window.location.href : ""}
      />
      <TextCta
        id="newsletter-button"
        className="focus:outline-white"
        kind={"white"}
        icon={<Arrow />}
        label={fields.buttonText ?? "Sign Up"}
        type="submit"
      />
    </Form>
  );
}
